import { useCallback, useState } from 'react';
import { Button, Dropdown } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDashboardAlerts } from '@/entities/dashboard/api/hooks';
import type { DashboardAlert } from '@/entities/dashboard/model/dashboardSchema';
import s from './HeaderAlertsBell.module.scss';

const alertClassByType: Record<DashboardAlert['type'], string> = {
  err: s['root__alert_err'],
  warn: s['root__alert_warn'],
  ok: s['root__alert_ok'],
  info: s['root__alert_info'],
};

interface Props {
  tone?: 'default' | 'dark';
}

export function HeaderAlertsBell({ tone = 'default' }: Props) {
  const { data: alerts, isLoading } = useDashboardAlerts();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const popupRender = useCallback(() => {
    if (!alerts?.length) {
      return (
        <div className={s.root__panel}>
          <div className={s.root__empty}>Нет уведомлений</div>
        </div>
      );
    }
    return (
      <div className={s.root__panel}>
        <div className={s.root__title}>Уведомления</div>
        <ul className={s.root__list}>
          {alerts.map((a, i) => (
            <li
              key={i}
              className={`${s.root__item} ${alertClassByType[a.type]} ${a.linkTo ? s.root__item_link : ''}`}
              onClick={() => {
                if (!a.linkTo) return;
                navigate(a.linkTo);
                setOpen(false);
              }}
              role={a.linkTo ? 'button' : undefined}
              tabIndex={a.linkTo ? 0 : undefined}
              onKeyDown={(e) => {
                if (!a.linkTo) return;
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                navigate(a.linkTo);
                setOpen(false);
              }}
            >
              {a.message}
            </li>
          ))}
        </ul>
      </div>
    );
  }, [alerts, navigate]);

  return (
    <Dropdown open={open} onOpenChange={setOpen} trigger={['click']} placement="bottomRight" popupRender={popupRender}>
      <Button
        type="text"
        className={tone === 'dark' ? `${s.root__trigger} ${s['root__trigger_dark']}` : s.root__trigger}
        aria-label="Уведомления"
        loading={isLoading}
        icon={<BellOutlined size={20} style={{ color: 'white', strokeWidth: 1.5 }} />}
      />
    </Dropdown>
  );
}
